import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "lucide-react";
import { AppSidebarHeader, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { SidebarPrimaryNavStatic } from "./SidebarPrimaryNav";
import { SessionSwitcherHint } from "./SessionSwitcherHint";
import { SESSION_FOOTER_CLASS, SESSION_FOOTER_LABEL_CLASS, SESSION_ITEM_INDICATOR_GUTTER_CLASS, SESSION_ITEM_ROW_CLASS, SESSION_ITEM_ROW_WRAP_CLASS, SESSION_SECTION_HEADER_BOX_CLASS } from "./sessionSidebarChrome";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
export const SESSION_SIDEBAR_EDGE_CLASS = "shadow-[inset_-1px_0_0_0_var(--color-border)]";
interface SidebarSkeletonProps {
  variant?: "full" | "inline";
}
const TITLE_WIDTHS = ["w-[78%]", "w-[55%]", "w-[68%]", "w-[44%]", "w-[62%]"];
function SessionItemSkeleton(t0) {
  const $ = _c(11);
  const {
    showAuthor,
    titleWidth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={SESSION_ITEM_INDICATOR_GUTTER_CLASS} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `h-3.5 ${titleWidth} rounded`;
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="flex h-[22px] items-center"><Skeleton className={t2} /></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== showAuthor) {
    t4 = showAuthor && <Skeleton className="h-2.5 w-10 rounded" />;
    $[3] = showAuthor;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-2.5 w-12 rounded" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4) {
    t6 = <div className="flex h-3 items-center gap-2">{t4}{t5}</div>;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <div className={SESSION_ITEM_ROW_WRAP_CLASS}><div className={SESSION_ITEM_ROW_CLASS} data-slot="session-item-skeleton">{t1}<div className="min-w-0 flex-1 overflow-hidden">{t3}{t6}</div></div></div>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function SectionHeaderSkeleton(t0) {
  const $ = _c(8);
  const {
    label,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(SESSION_SECTION_HEADER_BOX_CLASS, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="type-uppercase-title text-neutral-500 dark:text-neutral-300">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown className="size-3 text-neutral-500 dark:text-neutral-400" aria-hidden={true} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function SidebarBodyContent() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><SectionHeaderSkeleton label="My Sessions" className="pt-3" /><SessionSwitcherHint />{TITLE_WIDTHS.map(_temp)}<SectionHeaderSkeleton label="Team Sessions" className="pt-5" />{TITLE_WIDTHS.map(_temp2)}</>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(w_0, i_0) {
  return <SessionItemSkeleton key={`team-${i_0}`} showAuthor={true} titleWidth={w_0} />;
}
function _temp(w, i) {
  return <SessionItemSkeleton key={`my-${i}`} titleWidth={w} />;
}
function SidebarBody() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex-1 relative min-h-0"><div className="h-full overflow-hidden"><div className="px-2 py-1"><SidebarBodyContent /></div></div><div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--sidebar)] to-transparent" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function SidebarSkeleton(t0) {
  const $ = _c(14);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "full" : t1;
  const isHydrated = useIsHydrated();
  if (variant === "inline") {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SidebarBodyContent />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== isHydrated) {
    t2 = isHydrated ? readLeftSidebarSkeletonLayout() : null;
    $[1] = isHydrated;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const layout = t2;
  if (layout && !layout.visible) {
    return null;
  }
  let t3;
  if ($[3] !== layout) {
    t3 = layout ? {
      width: layout.width
    } : undefined;
    $[3] = layout;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = layout ? "flex" : "hidden min-[1018px]:flex w-[330px]";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("relative flex-col bg-sidebar overflow-hidden pointer-events-none flex-shrink-0", SESSION_SIDEBAR_EDGE_CLASS, t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AppSidebarHeader title="[code]smith" actions={<PanelTogglePlaceholder />} />;
    t7 = <SidebarPrimaryNavStatic />;
    t8 = <SidebarBody />;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={SESSION_FOOTER_CLASS}><span className={SESSION_FOOTER_LABEL_CLASS}>Archived</span></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t3 || $[12] !== t5) {
    t10 = <div style={t3} className={t5} data-slot="assistant-sidebar-skeleton">{t6}{t7}{t8}{t9}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
}